import { FC, Fragment, useContext, useState } from 'react';
import { CgChevronDown } from 'react-icons/cg';
import { Button, Menu, Stack } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useOcrLabIdVerification,
  useOcrLabIndividuals,
} from '~/core/shared/api/plugin';
import { Dialog } from '~/core/shared/components';
import {
  CategorizedIndividual,
  Individual,
  OcrLabIdVerificationRequest,
} from '~/core/shared/types';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../../Layout.component';

export const IDVerseModal: FC = () => {
  const { actionContexts, applicationUuid } = useContext(ApplicationContext);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const ocrLabModalApi: UseOpenCloseReturn = actionContexts[key].modal;

  const [selectedIndividual, setSelectedIndividual] = useState<Individual>();

  const { individuals, isLoading: isLoadingIndividuals } = useOcrLabIndividuals(
    applicationUuid,
    { enabled: ocrLabModalApi.open },
  );

  const { postOcrLabIdVerification, isPending: isLoadingVerification } =
    useOcrLabIdVerification();

  const sendIdVerification = async (): Promise<void> => {
    if (!selectedIndividual) {
      return;
    }

    const data: OcrLabIdVerificationRequest = {
      individualUuid: selectedIndividual.id,
      uuid: applicationUuid,
    };

    try {
      await postOcrLabIdVerification(data);
      ocrLabModalApi.onClose();
    } catch {
      // Error is handled in useOcrLabIdVerification
    }
  };

  const selectIndividual = (individual: Individual) => {
    setSelectedIndividual(individual);
  };

  const renderMenuItem = (item: CategorizedIndividual) => (
    <Menu.Item
      minH="48px"
      onClick={() => selectIndividual(item)}
      value={item.id}
    >
      <span>
        {item.firstName} {item.lastName}
      </span>
    </Menu.Item>
  );

  const renderMenuItemsByType = (type: string) =>
    individuals?.filter((item) => item.type === type).map(renderMenuItem);

  const uniqueTypes: string[] = [
    ...new Set(individuals?.map((item) => item.type)),
  ];

  return (
    <Dialog
      header="IDVerse - Verify identity"
      onOpenChange={ocrLabModalApi.onOpenChange}
      open={ocrLabModalApi.open}
      size="lg"
    >
      <Stack gap={4}>
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button
              bg="white"
              borderColor="#CBD5E0"
              borderRadius="md"
              color="#000000"
              fontSize="14px"
              loading={isLoadingIndividuals}
              size="md"
              variant="outline"
              width="100%"
            >
              {(selectedIndividual &&
                `${selectedIndividual?.firstName} ${selectedIndividual?.lastName}`) ||
                'Select Individual'}
              <CgChevronDown />
            </Button>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content maxH={200} overflowY="auto">
              {uniqueTypes.map((type, index) => (
                <Fragment key={type}>
                  <Menu.ItemGroup>
                    <Menu.ItemGroupLabel>{type}</Menu.ItemGroupLabel>
                    {renderMenuItemsByType(type)}
                  </Menu.ItemGroup>
                  {index < uniqueTypes.length - 1 && <Menu.Separator />}
                </Fragment>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>

        <Stack direction="row" justifyContent="end">
          <Button
            colorPalette="green"
            disabled={!selectedIndividual}
            fontSize="md"
            fontWeight="normal"
            loading={isLoadingVerification}
            onClick={sendIdVerification}
          >
            Send
          </Button>
          <Button
            colorPalette="gray"
            fontSize="md"
            fontWeight="normal"
            onClick={ocrLabModalApi.onClose}
            variant="outline"
          >
            Close
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
